import { Box, Skeleton, Grid } from '@mui/material';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import strings from '../../Globalization';
import consts from '../../consts';
import { useSetState, useUniqueEffect } from '../../hooks';
import connectionService from '../../services/Connection';
import util from '../../util/index';
import TopFilter from '../../views/TopFilter';
import EquipmentListComponent from './DashboardComponents/EquipmentList';
import EquipmentTypeComponent from './DashboardComponents/EquipmentType';
import StatisticsComponent from './DashboardComponents/Statistics';


const DashboardCompoment = ({ equipmentTypeId }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const [dashboardList, setDashboardList] = useSetState({
		success: false,
		loading: false,
		combos: {},
		records: [],
		recordCount: 0,
		preferences: null,
		selectedView: 'list'
	}
	);

	const fromModality = location.pathname === '/modalitydashboard';
	const tableId = fromModality ? 'ModalityDashboard' : 'MainDashboard';
	const loadRelease = util.localData?.loginData
		? consts.isNodeServer
			? util.localData.LoadReleaseIdAccess || util.localData.loginData?.tags?.LoadReleaseId
			: Number(util.localData.LoadReleaseIdAccess || util.localData?.loginData?.tags?.LoadReleaseId)
		: false;
	const [extraParams, setExtraParams] = useSetState({
		locationId: 0,
		equipmentTypeId: 0,
		disconnected: true,
		connected: true,
		equipmentActive: true,
		equipmentInActive: false,
	});
	const [equipmentActive, setEquipmentActive] = useState(true);
	const [equipmentInActive, setEquipmentInActive] = useState(false);

	const fetchData = useCallback(
		async (options = { backgroundApi: false }) => {
			let params = { ...extraParams, action: 'otherAction', otherAction: 'GetDashboard' };
			params.equipmentTypeId = fromModality ? searchParams.get('EquipmentTypeId') : options.equipmentTypeId;
			if (baseFilters.length > 0) {
				params.filters = JSON.stringify(baseFilters);
			}
			let formData = new FormData();
			for (const [key, value] of Object.entries(params)) {
				formData.append(key, value);
			}
			setDashboardList({ loading: true });
			await fetch(connectionService.DashBoardEquipment, {
				method: 'POST',
				headers: {
					'X-Background-Request': options.backgroundApi
				},
				body: formData
			})
				.then((res) => res.json())
				.then((json) => {
					const data = json?.data || {};
					setDashboardList({
						...data,
						success: true,
						otherAction: 'GetDashboard',
						loading: false
					});
				})
				.catch((error) => {
					setDashboardList({ success: false, combos: {}, records: [], recordsCount: 0, error });
				})
				.finally(() => {
					setDashboardList({ loading: false });
				});
		},
		[equipmentActive, equipmentInActive, fromModality, extraParams]
	);

	useUniqueEffect(() => {
		fetchData({ ...extraParams });
	}, [extraParams])

	const fetchPreferences = useCallback(() => {

		const formData = new FormData();
		formData.append('id', tableId);
		formData.append('action', 'list');

		setDashboardList({ loading: true });
		return fetch(connectionService.GridPreferenceManager, {
			method: 'POST',
			headers: {
				'X-Background-Request': 'true'
			},
			body: formData,
			credentials: 'include'
		})
			.then((res) => res.json())
			.then((json) => JSON.parse(json?.preferences[0]?.prefValue || '{}'))
			.catch(err => {

			})
			.finally(() => setDashboardList({ loading: false }));
	}, [fromModality]);

	const loadDataWithPreferences = ( backgroundApi = true ) => {
		fetchPreferences().then((preferences) => {
			if (preferences) {
				preferences.equipmentActive = true;
				setDashboardList({ preferences });
				fetchData({ ...preferences?.extraParams, backgroundApi });
			}

		});
	};

	useEffect(() => {
		let interval = setInterval(() => {
			loadDataWithPreferences();
		}, 300000)
		loadDataWithPreferences();

		return () => {
			clearInterval(interval)
		}
	}, []);

	const baseFilters = useMemo(() => {
		let baseFiltersVal = [];
		for (let [key, value] of Object.entries(extraParams)) {
			switch (key.toLowerCase()) {
				case 'disconnected':
				case 'equipmentinactive':
					if (!value) {
						baseFiltersVal.push({
							column: key.toLowerCase() === 'disconnected' ? 'IsDisConnected' : 'InActive',
							value: value,
							type: 'bool',
							operator: '$eq'
						});
					}
					break;
				case 'connected':
				case 'equipmentactive':
					if (!value) {
						baseFiltersVal.push({
							column: key.toLowerCase() === 'connected' ? 'IsConnected' : 'IsActive',
							value: value,
							type: 'bool',
							operator: '$eq'
						});
					}
					break;
				default:
					if (value !== 0) {
						baseFiltersVal.push({ column: key, value: value, type: 'int', operator: '$eq' });
					}
					break;
			}
		}
		return baseFiltersVal;
	}, [extraParams]);

	const onViewAll = (view, event) => {
		if (fromModality) {
			let modalityId = equipmentTypeId || 0;
			navigate(view, { state: { modalityId } });
		} else {
			navigate(view);
		}
	};

	let isDryingCabinet = !!dashboardList?.EquipmentTypeName && (dashboardList.EquipmentTypeName.toUpperCase() === consts.EquipmentTypeName.DRYINGCABINET);
	const handleOnFilterChange = useCallback((filters) => {
		setExtraParams({ ...filters });
	}, [extraParams]);
	const savePreferences = useCallback((prefs) => {
		const preferences = dashboardList.preferences;
		const prefValue = {
			...preferences,
			...prefs,
			extraParams
		};
		const formData = new FormData();
		formData.append('id', tableId);
		formData.append('prefId', 0);
		formData.append('prefName', tableId);
		formData.append('isDefault', true);
		formData.append('prefValue', JSON.stringify(prefValue));
		formData.append('prefDesc', '');
		formData.append('action', 'save');
		fetch(connectionService.GridPreferenceManager, {
			method: 'POST',
			body: formData,
			credentials: 'include'
		})
			.then((res) => res.json())
			.then((json) => {
				setDashboardList({ preferences: JSON.parse(json?.data?.prefValue || '{}') })
			})
			.catch((err) => {
				Swal.fire({
					titleText: strings.ErrorTitle,
					text: strings.ErrorUpdate,
					icon: 'error',
					confirmButtonText: strings.Ok,
					confirmButtonColor: '#3085d6',
					allowOutsideClick: false
				});
			});
	}, [dashboardList.preferences, extraParams]);

	const handleOnChangeSelectedView = (selectedView) => {
		setDashboardList(prevState => ({ preferences: { ...prevState.preferences, selectedView } }));
		savePreferences({ selectedView });
	};

	const handleOnChangeEquipmentActive = (equipmentActive) => {
		setDashboardList((prevState) => ({ preferences: { ...prevState.preferences, equipmentActive } }));
		savePreferences({ equipmentActive });
	};

	const handleOnChangeEquipmentInActive = (equipmentInActive) => {
		setDashboardList((prevState) => ({ preferences: { ...prevState.preferences, equipmentInActive } }));
		savePreferences({ equipmentInActive });
	};


	return (
		<Grid container>
			<Grid size={12}>
				<TopFilter extraParams={extraParams} onFilterChange={handleOnFilterChange} />
				<Grid container size={12}>
					{!isDryingCabinet && (
						<StatisticsComponent
							dashboardList={dashboardList}
							fromModality={fromModality}
							extraParams={extraParams}
							loadRelease={loadRelease}
						/>
					)}
				</Grid>
				{dashboardList.loading ? (
					<Box marginBottom={1}>
						<Skeleton variant="rounded" width={'100%'} height={240} animation="wave" />
					</Box>
				) : (
					<Grid container size={12}>
						{!fromModality && (
							<EquipmentTypeComponent
								extraParams={extraParams}
								dashboardList={dashboardList}
								fromModality={fromModality}
							/>
						)}
					</Grid>
				)}
				{dashboardList.loading ? (
					<Box marginBottom={1}>
						<Skeleton variant="rounded" width={'100%'} height={320} animation="wave" />
					</Box>
				) : (
					<Grid container size={12}>
						{dashboardList && (
							<EquipmentListComponent
								extraParams={extraParams}
								equipmentActive={dashboardList.preferences?.equipmentActive}
								onViewAll={onViewAll}
								baseFilters={baseFilters}
								setEquipmentActive={handleOnChangeEquipmentActive}
								setEquipmentInActive={handleOnChangeEquipmentInActive}
								equipmentInActive={dashboardList.preferences?.equipmentInActive}
								fromModality={fromModality}
								dashboardList={dashboardList}
								selectedView={dashboardList.preferences?.selectedView || 'list'}
								onChangeView={handleOnChangeSelectedView}
							/>
						)}
					</Grid>
				)}
			</Grid>
		</Grid>
	);
};

export default DashboardCompoment;
